/**
 * Copyright (c) 2024-2026 Creative Agentics Limited. All rights reserved.
 * Proprietary and confidential. Unauthorised use, reproduction or distribution
 * of this software, in whole or in part, is strictly prohibited.
 * See LICENSE.txt for full terms.
 */

/**
 * SharedFile (v1_463) — the read-only page an external party (a bank, an
 * auditor, a counterparty's compliance team) sees at /shared?token=...
 *
 * Everything here is display only. The slice comes from /api/share-data,
 * which filters by the officer's chosen scope SERVER-SIDE, so this page can
 * only ever render what the officer put in scope. Nothing internal — red
 * flags, findings, notes, recommendations — is ever in the payload. The page
 * is gated by the same OTP flow as the client portal and review links: a
 * six-digit code to the named recipient's inbox, a 24-hour browser pass, and
 * every verified access written to the case notes.
 */

import { useState, useEffect } from "react";
import { financialRangeRows } from "./shared/financial-ranges.js";

const S = {
  page: { minHeight: "100vh", background: "#f3f4f6", fontFamily: "Arial, Helvetica, sans-serif", color: "#111827" },
  card: { background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: 12, padding: "22px 26px", marginBottom: 16, boxShadow: "0 1px 3px rgba(0,0,0,0.04)" },
  h2: { fontSize: 15, fontWeight: 700, color: "#0f766e", marginBottom: 14, textTransform: "uppercase", letterSpacing: "0.5px" },
  row: { display: "flex", justifyContent: "space-between", gap: 16, padding: "7px 0", borderBottom: "1px solid #f3f4f6", fontSize: 13.5 },
  k: { color: "#6b7280", flex: "0 0 220px" },
  v: { color: "#111827", textAlign: "right", flex: 1, wordBreak: "break-word" }
};

const Row = ({ k, v }) => (v === null || v === undefined || v === "") ? null : (
  <div style={S.row}><span style={S.k}>{k}</span><span style={S.v}>{String(v)}</span></div>
);

export default function SharedFile() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [otpGate, setOtpGate] = useState(null); // { token, maskedEmail } | null
  const [otpCode, setOtpCode] = useState("");
  const [otpError, setOtpError] = useState(null);
  const [otpBusy, setOtpBusy] = useState(false);
  const [otpResendIn, setOtpResendIn] = useState(0);

  useEffect(() => {
    if (otpResendIn <= 0) return;
    const t = setTimeout(() => setOtpResendIn(n => n - 1), 1000);
    return () => clearTimeout(t);
  }, [otpResendIn]);

  const otpRequestCode = async (tok) => {
    setOtpError(null);
    try {
      const r = await fetch("/api/otp-request", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ scope: "share", token: tok }) });
      const d = await r.json().catch(() => ({}));
      if (r.ok && d.sent) { setOtpGate(g => ({ ...(g || { token: tok }), maskedEmail: d.maskedEmail || (g && g.maskedEmail) })); setOtpResendIn(30); }
      else if (!r.ok) setOtpError(d.error || "The code could not be sent. Please try again shortly.");
    } catch (_) { setOtpError("The code could not be sent. Please check your connection and try again."); }
  };

  const load = async (tok) => {
    try {
      const r = await fetch("/api/share-data", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ token: tok }) });
      const d = await r.json().catch(() => null);
      if (r.status === 409 && d && d.redirect) { window.location.replace(d.redirect); return; }
      if (r.status === 401 && d && d.otpRequired) {
        setOtpGate(g => g || { token: tok, maskedEmail: null });
        otpRequestCode(tok);
        setLoading(false);
        return;
      }
      if (!r.ok || !d || !d.success) { setError((d && d.error) || "The shared file could not be loaded."); setLoading(false); return; }
      setData(d);
      setLoading(false);
    } catch (_) {
      setError("The shared file could not be loaded. Please check your connection and try again.");
      setLoading(false);
    }
  };

  const otpVerifyCode = async () => {
    if (!otpGate || otpBusy) return;
    setOtpBusy(true); setOtpError(null);
    try {
      const r = await fetch("/api/otp-verify", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ scope: "share", token: otpGate.token, code: otpCode.trim() }) });
      const d = await r.json().catch(() => ({}));
      if (r.ok && d.ok) {
        const tok = otpGate.token;
        setOtpGate(null); setOtpCode(""); setLoading(true);
        load(tok);
      } else {
        setOtpError(d.error || "That code did not verify. Please try again.");
        if (d.requestNew) setOtpCode("");
      }
    } catch (_) { setOtpError("Verification failed. Please check your connection and try again."); }
    finally { setOtpBusy(false); }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    if (!token) { setError("This link is not valid."); setLoading(false); return; }
    load(token);
  }, []); // one-shot bootstrap; verify re-runs load directly

  if (otpGate) return (
    <div style={{ ...S.page, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: 14, padding: "36px 40px", width: 440, maxWidth: "94%", textAlign: "center", boxShadow: "0 4px 24px rgba(0,0,0,0.06)" }}>
        <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>Confirm it's you</div>
        <div style={{ fontSize: 13.5, color: "#6b7280", lineHeight: 1.7, marginBottom: 22 }}>
          This file is confidential. We have emailed a 6-digit code to {otpGate.maskedEmail ? <span style={{ color: "#0d9488", fontWeight: 600 }}>{otpGate.maskedEmail}</span> : "the address this file was shared with"}. Enter it below to open it.
        </div>
        <input
          value={otpCode}
          onChange={e => { setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6)); setOtpError(null); }}
          onKeyDown={e => { if (e.key === "Enter" && otpCode.length === 6) otpVerifyCode(); }}
          inputMode="numeric"
          autoComplete="one-time-code"
          autoFocus
          placeholder="••••••"
          style={{ width: "100%", boxSizing: "border-box", background: "#f9fafb", border: "1px solid #d1d5db", borderRadius: 10, color: "#111827", fontSize: 28, fontWeight: 600, letterSpacing: "12px", textAlign: "center", padding: "13px 0 13px 12px", outline: "none", marginBottom: 14 }}
        />
        {otpError && <div style={{ fontSize: 12.5, color: "#dc2626", lineHeight: 1.6, marginBottom: 12 }}>{otpError}</div>}
        <button
          onClick={otpVerifyCode}
          disabled={otpBusy || otpCode.length !== 6}
          style={{ width: "100%", background: otpBusy || otpCode.length !== 6 ? "#99d6d0" : "#0d9488", color: "#ffffff", border: "none", padding: "13px 0", borderRadius: 8, fontWeight: 700, fontSize: 14.5, cursor: otpBusy || otpCode.length !== 6 ? "default" : "pointer", marginBottom: 14 }}
        >{otpBusy ? "Verifying..." : "Open the file"}</button>
        <button
          onClick={() => otpRequestCode(otpGate.token)}
          disabled={otpResendIn > 0}
          style={{ background: "none", border: "none", color: otpResendIn > 0 ? "#9ca3af" : "#0d9488", fontSize: 13, cursor: otpResendIn > 0 ? "default" : "pointer", textDecoration: "underline" }}
        >{otpResendIn > 0 ? `Send a new code (${otpResendIn}s)` : "Send a new code"}</button>
        <div style={{ marginTop: 18, fontSize: 11.5, color: "#9ca3af", lineHeight: 1.6 }}>The code expires after 10 minutes. Once verified, this browser stays verified for 24 hours.</div>
      </div>
    </div>
  );

  if (loading) return (
    <div style={{ ...S.page, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ color: "#6b7280", fontSize: 14 }}>Loading the shared file...</div>
    </div>
  );

  if (error) return (
    <div style={{ ...S.page, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ textAlign: "center", maxWidth: 420 }}>
        <div style={{ color: "#dc2626", marginBottom: 8, fontWeight: 600, fontSize: 15 }}>{error}</div>
        <div style={{ color: "#6b7280", fontSize: 13, lineHeight: 1.6 }}>Please contact the firm that shared this file with you if you believe this is in error.</div>
      </div>
    </div>
  );

  const p = data.profile || {};
  const cd = p.companyDetails || {};
  const partyTable = (title, list) => !list || !list.length ? null : (
    <div style={{ marginTop: 14 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: "#374151", marginBottom: 6 }}>{title}</div>
      {list.map((x, i) => (
        <div key={i} style={S.row}>
          <span style={{ ...S.k, flex: 1, color: "#111827" }}>{x.name}{x.corporateUBO ? " (corporate)" : ""}</span>
          <span style={{ ...S.v, color: "#6b7280" }}>{[x.role, x.nationality || x.countryOfResidence, x.ownership ? x.ownership + "%" : null].filter(Boolean).join(" · ")}</span>
        </div>
      ))}
    </div>
  );

  return (
    <div style={S.page}>
      <div style={{ maxWidth: 760, margin: "0 auto", padding: "36px 20px 60px" }}>
        <div style={{ marginBottom: 22 }}>
          <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4 }}>{data.firmName ? `Shared by ${data.firmName}` : "Shared compliance file"} · Read only</div>
          <div style={{ fontSize: 24, fontWeight: 700 }}>{data.companyName || "Client file"}</div>
          <div style={{ fontSize: 13, color: "#6b7280", marginTop: 6 }}>
            Case status: <span style={{ fontWeight: 700, color: data.caseStatus === "approved" ? "#0f766e" : data.caseStatus === "declined" ? "#dc2626" : "#92400e" }}>{data.caseStatus === "approved" ? `Approved${data.approvedAt ? " " + String(data.approvedAt).slice(0, 10) : ""}` : data.caseStatus === "declined" ? "Declined" : "Under review"}</span>
          </div>
        </div>

        <div style={S.card}>
          <div style={S.h2}>Company profile</div>
          <Row k="Legal name" v={cd.legalName} />
          <Row k="Registration number" v={cd.registrationNumber} />
          <Row k="Date of incorporation" v={cd.dateOfIncorporation} />
          <Row k="Jurisdiction" v={cd.jurisdiction} />
          <Row k="Registered address" v={cd.registeredAddress} />
          <Row k="Business" v={cd.businessDescription} />
          <Row k="Client type" v={p.clientType} />
          {p.regulatoryStatus && <>
            <Row k="Regulated" v={p.regulatoryStatus.regulated === true ? "Yes" : p.regulatoryStatus.regulated === false ? "No" : null} />
            <Row k="Regulator" v={p.regulatoryStatus.regulator} />
            <Row k="Licence number" v={p.regulatoryStatus.licenceNumber} />
            <Row k="Licence status" v={p.regulatoryStatus.licenceStatus} />
          </>}
          {p.amlSupervision && <>
            <Row k="AML supervised" v={p.amlSupervision.supervised === true ? "Yes" : p.amlSupervision.supervised === false ? "No" : null} />
            <Row k="AML supervisory body" v={p.amlSupervision.supervisoryBody} />
          </>}
          {partyTable("Directors", p.directors)}
          {partyTable("Beneficial owners", p.ubos)}
          {partyTable("Authorised signatories", p.authorisedSignatories)}
          {p.activityProfile && (p.activityProfile.customerTypes || p.activityProfile.sectors || p.activityProfile.geographiesServed || p.activityProfile.currencies) && <div style={{ marginTop: 14 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#374151", marginBottom: 6 }}>Activity profile</div>
            <Row k="Customer types" v={p.activityProfile.customerTypes} />
            <Row k="Sectors" v={p.activityProfile.sectors} />
            <Row k="Geographies served" v={p.activityProfile.geographiesServed} />
            <Row k="Currencies" v={p.activityProfile.currencies} />
          </div>}
          {(() => {
            // Client-indicated financial ranges (v1_646) — labelled so they never read as verified.
            const fr = financialRangeRows(p.financialRanges);
            if (!fr.length) return null;
            return (
              <div style={{ marginTop: 14 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#374151", marginBottom: 6 }}>Financials (client indicated)</div>
                {fr.map(r => <Row key={r.key} k={r.label.replace(" (client indicated)", "")} v={r.value} />)}
              </div>
            );
          })()}
        </div>

        {data.screening && (
          <div style={S.card}>
            <div style={S.h2}>Screening and verification summary</div>
            {(data.screening.identityVerifications || []).length === 0 && (data.screening.walletScreenings || []).length === 0 && <div style={{ fontSize: 13, color: "#6b7280" }}>No screening records on this file.</div>}
            {(data.screening.identityVerifications || []).map((v, i) => (
              <div key={"iv" + i} style={S.row}>
                <span style={{ ...S.k, flex: 1, color: "#111827" }}>{v.name || "Identity verification"}</span>
                <span style={{ ...S.v, color: /approv|green|complete/i.test(v.status || "") ? "#0f766e" : "#6b7280", fontWeight: 600 }}>{v.status || "pending"}{v.at ? " · " + String(v.at).slice(0, 10) : ""}</span>
              </div>
            ))}
            {(data.screening.walletScreenings || []).map((w, i) => (
              <div key={"ws" + i} style={S.row}>
                <span style={{ ...S.k, flex: 1, color: "#111827" }}>Wallet screening</span>
                <span style={{ ...S.v, color: "#6b7280", fontWeight: 600 }}>{w.rating || "screened"}{w.at ? " · " + String(w.at).slice(0, 10) : ""}</span>
              </div>
            ))}
          </div>
        )}

        {data.documents && (
          <div style={S.card}>
            <div style={S.h2}>Document register</div>
            <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 10, lineHeight: 1.6 }}>Documents held on the file, by name and assessment status. The underlying files are not available through this link.</div>
            {data.documents.length === 0 && <div style={{ fontSize: 13, color: "#6b7280" }}>No documents recorded.</div>}
            {data.documents.map((d, i) => (
              <div key={i} style={S.row}>
                <span style={{ ...S.k, flex: 1, color: "#111827" }}>{d.name}</span>
                <span style={{ ...S.v, color: "#6b7280" }}>{[d.status, d.at ? String(d.at).slice(0, 10) : null].filter(Boolean).join(" · ")}</span>
              </div>
            ))}
          </div>
        )}

        {data.risk && (data.risk.riskTier || data.risk.assessmentPending || data.risk.riskRationale) && (
          <div style={S.card}>
            <div style={S.h2}>Risk classification</div>
            <Row k="Risk tier" v={data.risk.riskTier || (data.risk.assessmentPending ? "Assessment pending" : null)} />
            {data.risk.riskRationale && <div style={{ fontSize: 13.5, color: "#374151", lineHeight: 1.7, marginTop: 10 }}>{data.risk.riskRationale}</div>}
          </div>
        )}

        <div style={{ fontSize: 11.5, color: "#9ca3af", lineHeight: 1.7, textAlign: "center", marginTop: 26 }}>
          This file was shared with {data.sharedWith || "you"} by {data.firmName || "the firm"} on a read-only basis and is confidential.
          {data.expiresAt ? ` The link expires on ${String(data.expiresAt).slice(0, 10)}.` : ""} Prepared via CompliantID.
        </div>
      </div>
    </div>
  );
}
